import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { ApiError } from '@/api/client'
import { AgentRow } from '@/components/AgentRow'
import { settingsCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Icon } from '@/ui/Icon/Icon'
import { IconButton } from '@/ui/IconButton/IconButton'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Menu, type MenuEntry } from '@/ui/Menu/Menu'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { RowList, RowListItem } from '@/ui/RowList/RowList'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { agentDefsApi } from '../../api/agents'
import { RefreshFailed } from '../../components/RefreshFailed'
import { ROUTES } from '../../constants/routes'
import { agentQueryKeys, useWorkspaceAgentsQuery } from '../../hooks/queries'
import { useToastStore } from '../../store/toast'
import type { AgentDefInfo, WorkspaceInfo } from '../../types/api'

type LifecycleKind = 'pause' | 'restore' | 'delete'
interface LifecycleAction {
  agentId: string
  kind: LifecycleKind
}

export function AgentsTab({ workspace }: { workspace: WorkspaceInfo }) {
  const wsId = workspace.workspace_id
  const canManage = workspace.role === 'owner' || workspace.role === 'admin'
  const qc = useQueryClient()
  const addToast = useToastStore((state) => state.addToast)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [busyAgentIds, setBusyAgentIds] = useState<Set<string>>(new Set())
  const [pendingLifecycle, setPendingLifecycle] = useState<
    Record<string, LifecycleAction>
  >({})
  const retryTimers = useRef(
    new Map<string, ReturnType<typeof globalThis.setTimeout>>(),
  )

  const agentsQuery = useWorkspaceAgentsQuery(wsId, {
    includeDeleted: canManage,
    staleTime: 30_000,
  })
  const showSkeleton = useSkeletonDelay(agentsQuery.isPending)
  const agentDefs = agentsQuery.data ?? []

  useEffect(
    () => () => {
      retryTimers.current.forEach((timer) => {
        globalThis.clearTimeout(timer)
      })
      retryTimers.current.clear()
    },
    [],
  )

  function clearRetry(agentId: string) {
    const timer = retryTimers.current.get(agentId)
    if (timer) globalThis.clearTimeout(timer)
    retryTimers.current.delete(agentId)
  }

  function updateCachedAgent(next: AgentDefInfo) {
    qc.setQueryData<AgentDefInfo[]>(
      agentQueryKeys.workspace(wsId, true),
      (current = []) => {
        const exists = current.some((d) => d.agent_id === next.agent_id)
        return exists
          ? current.map((d) => (d.agent_id === next.agent_id ? next : d))
          : [...current, next]
      },
    )
    qc.setQueryData<AgentDefInfo[]>(
      agentQueryKeys.workspace(wsId, false),
      (current = []) => {
        const without = current.filter((d) => d.agent_id !== next.agent_id)
        return next.status === 'deleted' ? without : [...without, next]
      },
    )
    qc.setQueryData(agentQueryKeys.detail(wsId, next.agent_id), next)
  }

  function invalidateAgents(agentId: string) {
    void qc.invalidateQueries({
      queryKey: agentQueryKeys.workspace(wsId, true),
    })
    void qc.invalidateQueries({
      queryKey: agentQueryKeys.workspace(wsId, false),
    })
    void qc.invalidateQueries({
      queryKey: agentQueryKeys.detail(wsId, agentId),
    })
  }

  async function runLifecycle(action: LifecycleAction) {
    clearRetry(action.agentId)
    setBusyAgentIds((s) => new Set(s).add(action.agentId))
    try {
      const response =
        action.kind === 'delete'
          ? await agentDefsApi.deleteWithMeta(wsId, action.agentId)
          : await agentDefsApi.updateWithMeta(wsId, action.agentId, {
              status: action.kind === 'pause' ? 'paused' : 'active',
            })
      updateCachedAgent(response.data)
      invalidateAgents(action.agentId)
      setConfirmDeleteId(null)

      if (response.status === 202) {
        setPendingLifecycle((c) => ({
          ...c,
          [action.agentId]: action,
        }))
        addToast(settingsCopy.agents.updatePending(response.data.name), 'info')
        const delay = Math.max(250, (response.retryAfterSeconds ?? 1) * 1_000)
        const timer = globalThis.setTimeout(() => {
          retryTimers.current.delete(action.agentId)
          void runLifecycle(action)
        }, delay)
        retryTimers.current.set(action.agentId, timer)
      } else {
        setPendingLifecycle((c) => {
          const next = { ...c }
          delete next[action.agentId]
          return next
        })
      }
    } catch (error) {
      addToast(
        settingsCopy.agents.lifecycleFailure(
          action.kind,
          error instanceof ApiError ? error.status : undefined,
        ),
        'error',
      )
    } finally {
      setBusyAgentIds((s) => {
        const next = new Set(s)
        next.delete(action.agentId)
        return next
      })
    }
  }

  const confirmDeleteAgent = agentDefs.find(
    (d) => d.agent_id === confirmDeleteId,
  )
  const returnTo = ROUTES.workspaceSettingsTab(wsId, 'agents')

  const newAgent = (
    <Button
      variant="ghost"
      href={ROUTES.agentNew(wsId)}
      state={{ wsId, returnTo }}
    >
      <Icon name="plus" size="icon-embedded" />
      {settingsCopy.agents.new}
    </Button>
  )

  function lifecycleLabel(action: LifecycleAction | undefined) {
    if (!action) return null
    return settingsCopy.agents.pending[action.kind]
  }

  function agentActions(
    def: AgentDefInfo,
    pending: LifecycleAction | undefined,
  ) {
    const busy = busyAgentIds.has(def.agent_id)
    const items: MenuEntry[] = []
    if (def.status === 'active') {
      items.push({
        id: 'pause',
        label: settingsCopy.agents.pause,
        onSelect: () =>
          void runLifecycle({ agentId: def.agent_id, kind: 'pause' }),
      })
    } else if (def.status === 'paused' || def.status === 'deleted') {
      items.push({
        id: 'restore',
        label: settingsCopy.agents.restore,
        onSelect: () =>
          void runLifecycle({ agentId: def.agent_id, kind: 'restore' }),
      })
    }
    if (def.status !== 'deleted') {
      if (items.length > 0) items.push({ type: 'separator', id: 'sep' })
      items.push({
        id: 'delete',
        label: settingsCopy.agents.delete,
        tone: 'danger',
        onSelect: () => setConfirmDeleteId(def.agent_id),
      })
    }
    return (
      <>
        {pending && (
          <Button
            variant="ghost"
            loading={busy}
            onClick={() => {
              clearRetry(pending.agentId)
              void runLifecycle(pending)
            }}
          >
            {settingsCopy.agents.retryNow}
          </Button>
        )}
        {def.status !== 'deleted' &&
          (busy || pending ? (
            <Button variant="ghost" disabled>
              {settingsCopy.agents.edit}
            </Button>
          ) : (
            <Button
              variant="ghost"
              href={ROUTES.agentEdit(wsId, def.agent_id)}
              state={{ agent: def, wsId, returnTo }}
            >
              {settingsCopy.agents.edit}
            </Button>
          ))}
        {items.length > 0 && (
          <Menu
            placement="bottom-end"
            trigger={
              <IconButton
                label={settingsCopy.agentActions(def.name)}
                disabled={busy || !!pending}
              >
                <Icon name="dots-horizontal" />
              </IconButton>
            }
            items={items}
          />
        )}
      </>
    )
  }

  return (
    <>
      <section aria-busy={agentsQuery.isPending}>
        <SectionHeader rule trailingSpacing="section">
          {settingsCopy.agents.heading}
        </SectionHeader>
        <Stack gap="space-12">
          {agentsQuery.isError && agentsQuery.data && (
            <RefreshFailed
              message={settingsCopy.refreshFailed.agents}
              onRetry={() => void agentsQuery.refetch()}
            />
          )}

          {agentsQuery.isPending ? (
            showSkeleton && <Skeleton lines={3} />
          ) : agentsQuery.isError && !agentsQuery.data ? (
            <Alert tone="danger">
              <Inline gap="space-8" align="baseline" wrap>
                <Text>{settingsCopy.loadFailed.agents}</Text>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void agentsQuery.refetch()}
                >
                  {settingsCopy.retry}
                </Button>
              </Inline>
            </Alert>
          ) : agentDefs.length === 0 ? (
            <>
              <EmptyState
                variant="inline"
                description={settingsCopy.agents.empty}
              />
              {canManage && (
                <RowList aria-label={settingsCopy.agents.listLabel}>
                  <RowListItem>{newAgent}</RowListItem>
                </RowList>
              )}
            </>
          ) : (
            <RowList aria-label={settingsCopy.agents.listLabel}>
              {agentDefs.map((def) => {
                const pending = pendingLifecycle[def.agent_id]
                return (
                  <AgentRow
                    key={def.agent_id}
                    agent={def}
                    pendingLabel={lifecycleLabel(pending)}
                    actions={canManage ? agentActions(def, pending) : undefined}
                  />
                )
              })}
              {canManage && <RowListItem>{newAgent}</RowListItem>}
            </RowList>
          )}
        </Stack>
      </section>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title={settingsCopy.agents.deleteDialog.title}
        tone="default"
        description={
          confirmDeleteAgent
            ? settingsCopy.agents.deleteDialog.description(
                confirmDeleteAgent.name,
              )
            : undefined
        }
        confirmLabel={settingsCopy.agents.deleteDialog.confirm}
        onConfirm={() => {
          if (confirmDeleteId) {
            void runLifecycle({ agentId: confirmDeleteId, kind: 'delete' })
          }
        }}
        onClose={() => setConfirmDeleteId(null)}
        loading={confirmDeleteId ? busyAgentIds.has(confirmDeleteId) : false}
      />
    </>
  )
}
